import Button from '@legacy-vfuk/core-button';
import { HeadingInfoSlot, HeadingSlot, ImageSlot, ParagraphSlot, TextStackSlot } from '@legacy-vfuk/core-card-builder';
import { getImage, getSrc } from 'gatsby-plugin-image';
import { FC, useMemo, useState } from 'react';

import InfoAndCta from '../SWPricingCard/CardElements/InfoAndCta';
import {
  CardInfoContainer,
  CenteredDiv,
  FCWrapper,
  FlippedWrapper,
  ImageAndTextListContainer,
  ImageAndTextWrapper,
  ImageListContainer,
  IncludeContentContainer,
  IncludesContainer,
  TopContainerDiv,
} from './SWLargePricingCard.styles';
import { SWLargePricingCardProps } from './SWLargePricingCard.types';
import { ProductBundle } from '@/types/Product.types';
import { ImageDataLikeProps } from '../../Sections/SWPricingContent/SWPricingContent.types';
import { CurrencySymbol } from '@uk-source-web/text-stack';
import handleTranslation from '@/helpers/handleTranslation';

const SWLargePricingCard: FC<SWLargePricingCardProps> = ({ item, itemStepName, mergedProduct, mainItem }) => {
  const [isFlipped, setIsFlipped] = useState<boolean>(false);
  const includes = handleTranslation('Includes', 'Incluído');
  const exVat = handleTranslation('ex VAT', 'sem IVA');

  const thirdEntryPrice = useMemo(() => {
    if (item) {
      return item.dxlInformation?.price[0].price;
    }
  }, [item]);

  if (!item) return <div data-testid="empty-card"></div>;

  const thumbnailIconSrc = getSrc(item.thumbnailIcon) || getImage(item.thumbnailIcon)?.placeholder?.fallback || '';
  const parsedIcons =
    item.cardItems?.map(iconItem => {
      return {
        icon: getSrc(iconItem.itemIcon) ?? getImage(iconItem.itemIcon)?.placeholder?.fallback ?? '',
        title: iconItem.itemDescription,
      };
    }) ?? [];

  return (
    <FCWrapper isFlipped={isFlipped} data-testid="large-pricing-card">
      {isFlipped ? (
        <FlippedWrapper isFlipped={isFlipped} data-testid="flipped-wrapper">
          <CardInfoContainer data-testid="flipped-card-info">
            <TopContainer item={item} thumbnailIcon={thumbnailIconSrc} />
            <IncludesContainer height="621px" data-testid="flipped-includes-container">
              <ImageAndTextListContainer data-testid="flipped-image-text-list">
                {parsedIcons.map((iconItem, index) => {
                  return (
                    <ImageAndTextWrapper key={iconItem.title} data-testid={`flipped-icon-item-${index}`}>
                      <ImageSlot
                        alt={`${iconItem.title} icon`}
                        xl={{
                          src: iconItem.icon,
                          width: '34px',
                          height: '34px',
                        }}
                        aspectRatio="16/9"
                        objectFit="cover"
                        slotWrapper={{ appearance: 'primary', fullWidth: true }}
                      />
                      <ParagraphSlot paragraph={{ children: iconItem.title, size: 2 }} />
                    </ImageAndTextWrapper>
                  );
                })}
              </ImageAndTextListContainer>
            </IncludesContainer>
            <CenteredDiv data-testid="flipped-centered-div">
              {item.dxlInformation && (
                <InfoAndCta
                  {...{ mainItem, itemStepName }}
                  item={{ ...item }}
                  buttonStyling={{}}
                  useSimplePrice={mergedProduct}
                />
              )}
            </CenteredDiv>
          </CardInfoContainer>
        </FlippedWrapper>
      ) : (
        <>
          {thumbnailIconSrc && <TopContainer item={item} thumbnailIcon={thumbnailIconSrc} />}
          <CardInfoContainer data-testid="card-info-container">
            <ParagraphSlot paragraph={{ children: item.cardDescription }} data-testid="card-description" />
            <CardInfoContainer flexDirection="row" data-testid="pricing-info-container">
              <HeadingInfoSlot
                heading={{ text: item.detailAndPricing?.firstEntryValue ?? '' }}
                size={1}
                prefix={item.detailAndPricing?.firstEntry}
                data-testid="first-entry-info"
              />
              <HeadingInfoSlot
                heading={{ text: item.detailAndPricing?.secondEntryValue ?? '' }}
                size={1}
                prefix={item.detailAndPricing?.secondEntry}
                data-testid="second-entry-info"
              />
            </CardInfoContainer>
            <ParagraphSlot
              paragraph={{ children: item.detailAndPricing?.thirdEntry, size: 1 }}
              data-testid="third-entry-text"
            />
            <TextStackSlot
              size={2}
              primaryTextStack={{
                currency: {
                  symbol: (item.detailAndPricing?.currencySymbol as CurrencySymbol) ?? '€',
                  justify: 'left',
                },
                text: {
                  main: thirdEntryPrice?.toString() ?? '00,00',
                  suffix2: exVat,
                },
              }}
              data-testid="price-text-stack"
            />
          </CardInfoContainer>
          <IncludesContainer height="345px" data-testid="includes-container">
            <IncludeContentContainer data-testid="include-content">
              <HeadingSlot heading={{ text: includes }} size={1} data-testid="includes-heading" />
              <ImageListContainer data-testid="icon-list">
                {parsedIcons.map((iconItem, index) => {
                  return (
                    <ImageSlot
                      key={iconItem.title}
                      alt={`${iconItem.title} icon`}
                      xl={{
                        src: iconItem.icon,
                        width: '48px',
                        height: '48px',
                      }}
                      aspectRatio="16/9"
                      objectFit="cover"
                      slotWrapper={{ appearance: 'primary', fullWidth: true }}
                      data-testid={`card-icon-${index}`}
                    />
                  );
                })}
              </ImageListContainer>
            </IncludeContentContainer>
          </IncludesContainer>
          {item.dxlInformation && (
            <InfoAndCta
              {...{ mainItem, itemStepName }}
              item={{ ...item }}
              buttonStyling={{}}
              useSimplePrice={mergedProduct}
              data-testid="info-and-cta"
            />
          )}
        </>
      )}
    </FCWrapper>
  );
};

const TopContainer = ({ item, thumbnailIcon }: { item: any; thumbnailIcon: string }) => (
  <TopContainerDiv data-testid="top-container">
    <ImageSlot
      alt={`${item?.item?.name} icon`}
      xl={{
        src: item?.thumbnailIcon,
        width: '64px',
        height: '64px',
      }}
      aspectRatio="16/9"
      objectFit="cover"
      slotWrapper={{ appearance: 'primary', fullWidth: true }}
      data-testid="thumbnail-icon"
    />
    <HeadingSlot heading={{ text: item?.item?.name }} data-testid="item-name-heading" />
  </TopContainerDiv>
);

export default SWLargePricingCard;
